<template>
    <div
        v-if="shouldShowWebPhone"
        class="webphone-container"
        :class="{
            'webphone-minimized': isMinimized,
            'webphone-maximized': !isMinimized,
            'webphone-dragging': isDragging,
            shouldShowContactOverlay:
                shouldShowContactOverlay && !isContactOverlayCollapsed && !isMinimized && foundContacts.length > 0,
        }"
        :style="minimizedStyle"
    >
        <!-- Minimized Mode -->
        <div
            v-if="isMinimized"
            class="webphone-minimized-button"
            @mousedown="startDrag"
            :class="{
                'status-connected': sipStatus.isRegistered && !isOnBreak,
                'status-on-break': isOnBreak,
                'status-calling': callState.status === 'calling',
                'status-ringing': callState.status === 'ringing',
                'incoming-call': callState.status === 'incoming',
            }"
        >
            <div class="status-indicator"></div>
            <i class="material-icons">
                {{ getMinimizedIcon() }}
            </i>
            <div v-if="callState.status === 'incoming'" class="incoming-call-info">
                <div class="caller-name">{{ callState.callerName || callState.callerNumber }}</div>
            </div>
        </div>

        <!-- Maximized Mode -->
        <div v-else class="webphone-maximized-wrapper" :style="maximizedWrapperStyle">
            <!-- Contact Overlay - Sol taraf -->
            <WebPhoneContactOverlay
                ref="contactOverlay"
                :phone-number="getActivePhoneNumber()"
                :is-visible="shouldShowContactOverlay"
                :is-collapsed="isContactOverlayCollapsed"
                @contacts-updated="handleContactsUpdated"
                class="contact-overlay-left"
                :class="{ collapsed: isContactOverlayCollapsed }"
            />

            <!-- WebPhone Content - Sağ taraf -->
            <div class="webphone-maximized-content">
                <!-- Header -->
                <div class="webphone-header" @mousedown="startDragMaximized">
                    <div class="header-left">
                        <div class="status-dropdown" :class="{ open: isStatusDropdownOpen }">
                            <div
                                class="status-display"
                                :class="{
                                    connected: sipStatus.isRegistered && !isOnBreak,
                                    'on-break': isOnBreak,
                                    disconnected: !sipStatus.isRegistered && !isOnBreak
                                }"
                                @click.stop="toggleStatusDropdown"
                            >
                                <div class="status-dot"></div>
                                <span>{{ getStatusText() }}</span>
                                <i class="material-icons dropdown-arrow">keyboard_arrow_down</i>
                            </div>

                            <div v-if="isStatusDropdownOpen" class="dropdown-menu">
                                <div
                                    v-if="sipStatus.isRegistered && !isOnBreak"
                                    class="dropdown-item break-option"
                                    @click.stop="goToBreakMode"
                                >
                                    <div class="status-dot break"></div>
                                    <span>Molaya Al</span>
                                </div>
                                <div
                                    v-if="isOnBreak"
                                    class="dropdown-item online-option"
                                    @click.stop="goOnline"
                                >
                                    <div class="status-dot connected"></div>
                                    <span>Online Ol</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="header-center">
                        <h3>Supsis Web Phone</h3>
                    </div>
                    <div class="header-right">
                        <button class="minimize-btn" @click="toggleMode">
                            <i class="material-icons">remove</i>
                        </button>
                    </div>
                </div>

                <!-- Content Tabs -->
                <div class="webphone-tabs" :class="{ disabled: isOnBreak }">
                    <button
                        class="tab-btn"
                        :class="{ active: activeTab === 'dialer', disabled: isOnBreak }"
                        @click="!isOnBreak && (activeTab = 'dialer')"
                        :disabled="isOnBreak"
                    >
                        <i class="material-icons">dialpad</i>
                        <span>Tuş Takımı</span>
                    </button>
                    <button
                        class="tab-btn"
                        :class="{ active: activeTab === 'history', disabled: isOnBreak }"
                        @click="!isOnBreak && (activeTab = 'history')"
                        :disabled="isOnBreak"
                    >
                        <i class="material-icons">history</i>
                        <span>Geçmiş</span>
                    </button>
                    <button
                        class="tab-btn"
                        :class="{ active: activeTab === 'contacts', disabled: isOnBreak }"
                        @click="!isOnBreak && (activeTab = 'contacts')"
                        :disabled="isOnBreak"
                    >
                        <i class="material-icons">contacts</i>
                        <span>Dahililer</span>
                    </button>
                </div>

                <!-- Tab Content -->
                <div class="webphone-content" :class="{ disabled: isOnBreak }">
                    <!-- Break Mode Overlay -->
                    <div v-if="isOnBreak" class="break-mode-overlay">
                        <div class="break-icon">
                            <i class="material-icons">coffee</i>
                        </div>
                        <h3>Mola Modundasınız</h3>
                        <p>Web telefonu kullanmak için online olmanız gerekiyor.</p>
                        <button class="online-button" @click="goOnline">
                            <i class="material-icons">play_arrow</i>
                            Online Ol
                        </button>
                    </div>

                    <!-- Dialer Tab -->
                    <WebPhoneDialer
                        v-if="activeTab === 'dialer' && !isOnBreak"
                        ref="dialerComponent"
                        :call-state="callState"
                        :dial-number="dialNumber"
                        :sip-status="sipStatus"
                        :extensions="extensions"
                        :mapping="$_.get(netGsmChannel, 'payload.mapping', {})"
                        @dial="handleDial"
                        @call="handleCall"
                        @hangup="handleHangup"
                        @answer="handleAnswer"
                        @reject="handleReject"
                        @mute="handleMute"
                        @hold="handleHold"
                        @transfer="handleTransfer"
                        @dtmf="handleDTMF"
                        @number-change="dialNumber = $event"
                        @play-dtmf-tone="playDTMFTone($event)"
                    />

                    <!-- Contact Toggle Button -->
                    <div
                        v-if="shouldShowContactOverlay && foundContacts.length > 0"
                        ref="contactToggleButton"
                        class="contact-overlay-toggle-container"
                    >
                        <div
                            class="toggle-button"
                            @click="() => (isContactOverlayCollapsed = !isContactOverlayCollapsed)"
                        >
                            <i class="material-icons">{{
                                isContactOverlayCollapsed ? "visibility" : "visibility_off"
                            }}</i>
                            <span>{{ isContactOverlayCollapsed ? "Kişileri Göster" : "Kişileri Gizle" }}</span>
                            <div class="contact-count">{{ foundContacts.length }}</div>
                        </div>
                    </div>

                    <!-- Call History Tab -->
                    <WebPhoneCallHistory
                        v-if="activeTab === 'history' && !isOnBreak"
                        :call-history="callHistory"
                        @call="handleCallFromHistory"
                        @clear-history="clearCallHistory"
                    />

                    <!-- Contacts Tab -->
                    <WebPhoneContacts
                        v-if="activeTab === 'contacts' && !isOnBreak"
                        :extensions="extensions"
                        :mapping="$_.get(netGsmChannel, 'payload.mapping', {})"
                        :call-state="callState"
                        @call="handleCallFromContacts"
                        @refresh-contacts="handleRefreshExtensions"
                    />
                </div>
            </div>
        </div>

        <!-- Incoming Call Notification (when minimized) -->
        <div v-if="isMinimized && callState.status === 'incoming'" class="incoming-call-notification">
            <div class="caller-info">
                <div class="caller-avatar">
                    <i class="material-icons">person</i>
                </div>
                <div class="caller-details">
                    <div class="caller-name">{{ callState.callerName || "Bilinmeyen" }}</div>
                    <div class="caller-number">{{ callState.callerNumber }}</div>
                </div>
            </div>
            <div class="call-actions">
                <button class="answer-btn" @click="handleAnswer">
                    <i class="material-icons">call</i>
                </button>
                <button class="reject-btn" @click="handleReject">
                    <i class="material-icons">call_end</i>
                </button>
            </div>
        </div>

        <!-- Audio Elements -->
        <audio ref="remoteAudio" autoplay :volume="1.0" :muted="false"></audio>
        <audio ref="ringtone" loop>
            <source
                src="https://upload.wikimedia.org/wikipedia/commons/transcoded/3/34/Sound_Effect_-_Door_Bell.ogg/Sound_Effect_-_Door_Bell.ogg.mp3"
                type="audio/mpeg"
            />
        </audio>
        <audio ref="ringbacktone" loop>
            <source src="https://bigsoundbank.com/UPLOAD/mp3/1617.mp3" type="audio/mpeg" />
        </audio>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { SimpleUser } from "sip.js/lib/platform/web";
import { Howl } from "howler";
import WebPhoneDialer from "../KirmiziSantral/components/WebPhoneDialer.vue";
import WebPhoneCallHistory from "../KirmiziSantral/components/WebPhoneCallHistory.vue";
import WebPhoneContacts from "../KirmiziSantral/components/WebPhoneContacts.vue";
import WebPhoneContactOverlay from "../KirmiziSantral/components/WebPhoneContactOverlay.vue";
import { WEBPHONE_NETGSM_API } from "../../../../api/webphone.netgsm";
import log2 from "../../../../utils/log2";
import dtmfGenerator from "../../../../utils/dtmfToneGenerator";
import SYSTEM_PUBSUB from "../../../../utils/systemPubSub";

const log = log2("WebPhoneNetGSM");

export default Vue.extend({
    name: "WebPhoneNetGSM",
    components: {
        WebPhoneDialer,
        WebPhoneCallHistory,
        WebPhoneContacts,
        WebPhoneContactOverlay,
    },
    data() {
        return {
            // UI State
            isMinimized: true,
            activeTab: "dialer",
            isDragging: false,
            minimizedPosition: { bottom: 140, right: 20 }, // Kırmızı Santral'dan farklı konum
            maximizedPosition: { bottom: 20, right: 20 },
            dragStart: { x: 0, y: 0 },
            dragThreshold: 5,

            // SIP State
            simpleUser: null,
            sipStatus: {
                isRegistered: false,
                connectionStatus: "Bağlantı bekleniyor...",
                extensionNumber: null,
                domain: null,
            },

            // Call State
            callState: {
                status: "idle", // idle, calling, ringing, incoming, connected, holding, held
                currentSession: null,
                isOnHold: false,
                isMuted: false,
                callerNumber: null,
                callerName: null,
                callStartTime: null,
                callDuration: 0,
            },

            // Dialer
            dialNumber: "",

            // Call History
            callHistory: [],

            // Timers
            callDurationTimer: null,
            retryTimer: null,
            extensions: [],

            // System PubSub
            systemPubSubSubscription: null,
            sounds: {
                incomingCall: new Howl({
                    src: [`./static/media/webphone/incoming-call.mp3`],
                    autoplay: false,
                    loop: true,
                    volume: 1,
                    onplayerror: (ex) => log.error("incomingCall sound error!", ex),
                }),
                ringback: new Howl({
                    src: [`./static/media/webphone/ringback.mp3`, `https://bigsoundbank.com/UPLOAD/mp3/1617.mp3`],
                    autoplay: false,
                    loop: true,
                    volume: 1,
                    onplayerror: (ex) => log.error("ringback sound error!", ex),
                }),
            },

            // Contact Overlay State
            isContactOverlayCollapsed: false,
            foundContacts: [],
            contactToggleElement: null,

            // SIP Config Cache
            _cachedSipConfig: null,
            _currentSipConfig: null, // Şu anda bağlı olan SIP config

            // Break Mode
            isOnBreak: false,
            isStatusDropdownOpen: false,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            channels: (state) => state.channel.channels,
            users: (state) => state.user.users?.data || [],
            netgsmHistory: (state) => state.user.netgsmHistory,
        }),
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
        }),
        netGsmChannel() {
            return this.channels.find((channel) => channel.type === "webphone-netgsm");
        },
        userExtensionMapping() {
            if (!this.netGsmChannel || !this.netGsmChannel.payload?.mapping) {
                return null;
            }

            const mapping = this.netGsmChannel.payload.mapping;
            const userExtension = Object.entries(mapping).find(([extensionId, userId]) => userId === this.user._id);

            return userExtension
                ? {
                      extensionNumber: userExtension[0],
                      userId: userExtension[1],
                  }
                : null;
        },
        shouldShowWebPhone() {
            return (
                this.isLoggedIn &&
                this.netGsmChannel &&
                this.userExtensionMapping &&
                this.netGsmChannel.payload?.domain &&
                this.netGsmChannel.payload?.websocketServer
            );
        },

        sipConfig() {
            if (!this.netGsmChannel || !this.userExtensionMapping) {
                return null;
            }

            const domain = this.netGsmChannel.payload.domain;
            const websocketServer = this.netGsmChannel.payload.websocketServer;
            const phoneNumber = this.netGsmChannel.payload.phoneNumber;
            const extensionNumber = this.userExtensionMapping.extensionNumber;
            const extension = this.getCurrentUserExtension();
            if (!extension) {
                log("netgsm sipConfig not found", extensionNumber);
                return null;
            }

            const newSipConfig = {
                server: websocketServer,
                domain: domain,
                username: `${extensionNumber}-${phoneNumber}`,
                aor: `sip:${extensionNumber}-${phoneNumber}@${domain}`,
                displayName: this.user.name,
                password: extension?.password,
            };

            // Önceki config ile karşılaştır, aynıysa cached değeri döndür
            if (this._cachedSipConfig && this._sipConfigsAreEqual(this._cachedSipConfig, newSipConfig)) {
                return this._cachedSipConfig;
            }

            log("netgsm sipConfig", newSipConfig);
            this._cachedSipConfig = newSipConfig;
            return newSipConfig;
        },
        minimizedStyle() {
            if (!this.isMinimized) return {};
            return {
                bottom: `${this.minimizedPosition.bottom}px`,
                right: `${this.minimizedPosition.right}px`,
            };
        },
        maximizedWrapperStyle() {
            if (this.isMinimized) return {};

            const baseWidth = 320; // WebPhone width
            const isContactsVisible =
                this.shouldShowContactOverlay && this.foundContacts.length > 0 && !this.isContactOverlayCollapsed;
            const contactWidth = isContactsVisible ? 300 : 0; // Contact overlay width (300px)
            const totalWidth = baseWidth + contactWidth;

            return {
                bottom: `${this.maximizedPosition.bottom}px`,
                right: `${this.maximizedPosition.right}px`,
                width: `${totalWidth}px`,
            };
        },
        shouldShowContactOverlay() {
            // Sadece maximized modda ve aktif arama durumunda overlay'i göster
            const activeStates = ["calling", "ringing", "incoming", "connected"];
            return this.shouldShowWebPhone && !this.isMinimized && activeStates.includes(this.callState.status);
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
            fetchUsers: "user/getUsers",
        }),
        handleContactsUpdated(contacts) {
            this.foundContacts = contacts;
        },
        fetchExtensions() {
            return WEBPHONE_NETGSM_API.extensions.list().then((response) => {
                this.extensions = response.extensions;
            });
        },

        async handleRefreshExtensions() {
            try {
                await this.fetchExtensions();
                this.$message.success("Dahili listesi başarıyla yenilendi");
            } catch (error) {
                log.error("Dahili listesi yenilenirken hata:", error);
                this.$message.error("Dahili listesi yenilenirken hata oluştu");
            }
        },

        // SIP Config Helper Methods
        _sipConfigsAreEqual(config1, config2) {
            if (!config1 || !config2) return false;

            return (
                config1.server === config2.server &&
                config1.domain === config2.domain &&
                config1.username === config2.username &&
                config1.aor === config2.aor &&
                config1.displayName === config2.displayName &&
                config1.password === config2.password
            );
        },

        // UI Methods
        toggleMode() {
            this.isMinimized = !this.isMinimized;
            if (!this.isMinimized) {
                if (this.callState.status === "incoming") {
                    // Auto-focus on incoming call
                    this.activeTab = "dialer";
                }
                // WebPhone açıldığında numara girme alanına auto focus
                this.focusDialerInput();
            }
        },

        // Break Mode Methods
        toggleStatusDropdown() {
            if (this.isOnBreak || this.sipStatus.isRegistered) {
                this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
            }
        },

        async goToBreakMode() {
            if (this.callState.status !== "idle") {
                this.$message.warning("Aktif çağrı varken molaya alınamaz!");
                return;
            }

            this.isOnBreak = true;
            this.isStatusDropdownOpen = false;

            // Websocket bağlantısını kapat
            this.disconnectSIP();

            this.$message.success("Mola moduna geçildi");
        },

        async goOnline() {
            this.isOnBreak = false;
            this.isStatusDropdownOpen = false;

            // Websocket bağlantısını yeniden başlat
            if (this.shouldShowWebPhone) {
                await this.initializeSIP();
            }

            this.$message.success("Online moda geçildi");
        },

        getStatusText() {
            if (this.isOnBreak) {
                return "Molada";
            } else if (this.sipStatus.isRegistered) {
                return "Bağlı";
            } else {
                return "Bağlantı Kesildi";
            }
        },

        disconnectSIP() {
            if (this.simpleUser) {
                this.simpleUser.disconnect();
                this.simpleUser = null;
            }
            this.sipStatus.isRegistered = false;
            this.sipStatus.connectionStatus = "Bağlantı kesildi";
            this._currentSipConfig = null;

            // Retry timer'ı durdur (mola modunda otomatik reconnect olmasın)
            if (this.retryTimer) {
                clearTimeout(this.retryTimer);
                this.retryTimer = null;
            }
        },

        // Click outside handler for dropdown
        handleClickOutside(event) {
            if (!this.$el || typeof this.$el.querySelector !== 'function') return;
            const dropdown = this.$el.querySelector('.status-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                this.isStatusDropdownOpen = false;
            }
        },

        focusDialerInput() {
            this.$nextTick(() => {
                if (this.activeTab === "dialer" && this.$refs.dialerComponent) {
                    // Dialer component'inde input'a focus için method çağır
                    if (this.$refs.dialerComponent.focusInput) {
                        this.$refs.dialerComponent.focusInput();
                    }
                }
            });
        },

        startDrag(event) {
            if (!this.isMinimized) return;

            this.dragStart = {
                x: event.clientX,
                y: event.clientY,
            };

            document.addEventListener("mousemove", this.handleDragMove);
            document.addEventListener("mouseup", this.handleDragEnd);
            event.preventDefault();
        },

        startDragMaximized(event) {
            if (this.isMinimized) return;

            // Eğer minimize butonuna tıklanmışsa drag başlatma
            if (event.target.closest(".minimize-btn")) {
                return;
            }

            // Eğer status dropdown'a tıklanmışsa drag başlatma
            if (event.target.closest(".status-dropdown")) {
                return;
            }

            this.dragStart = {
                x: event.clientX,
                y: event.clientY,
            };

            document.addEventListener("mousemove", this.handleDragMove);
            document.addEventListener("mouseup", this.handleDragEnd);
            event.preventDefault();
        },

        handleDragMove(event) {
            const deltaX = Math.abs(event.clientX - this.dragStart.x);
            const deltaY = Math.abs(event.clientY - this.dragStart.y);

            // Start dragging only if moved beyond threshold
            if (!this.isDragging && (deltaX > this.dragThreshold || deltaY > this.dragThreshold)) {
                this.isDragging = true;
            }

            if (this.isDragging) {
                if (this.isMinimized) {
                    // Calculate new position for minimized mode
                    const newRight = Math.max(
                        10,
                        Math.min(window.innerWidth - 70, window.innerWidth - event.clientX - 30)
                    );
                    const newBottom = Math.max(
                        10,
                        Math.min(window.innerHeight - 70, window.innerHeight - event.clientY - 30)
                    );

                    this.minimizedPosition = {
                        bottom: newBottom,
                        right: newRight,
                    };
                } else {
                    // Calculate new position for maximized mode
                    const webphoneWidth = 320;
                    const webphoneHeight = 650;
                    const newRight = Math.max(
                        10,
                        Math.min(
                            window.innerWidth - webphoneWidth - 10,
                            window.innerWidth - event.clientX - webphoneWidth / 2
                        )
                    );
                    const newBottom = Math.max(
                        10,
                        Math.min(
                            window.innerHeight - webphoneHeight - 10,
                            window.innerHeight - event.clientY - webphoneHeight / 2
                        )
                    );

                    this.maximizedPosition = {
                        bottom: newBottom,
                        right: newRight,
                    };
                }
            }
        },

        handleDragEnd(event) {
            const wasDragging = this.isDragging;

            document.removeEventListener("mousemove", this.handleDragMove);
            document.removeEventListener("mouseup", this.handleDragEnd);

            // If it wasn't dragging, treat as click
            if (!wasDragging) {
                this.toggleMode();
            }

            this.isDragging = false;
        },

        getMinimizedIcon() {
            switch (this.callState.status) {
                case "calling":
                    return "call";
                case "ringing":
                    return "ring_volume";
                case "incoming":
                    return "call_received";
                case "connected":
                    return "call";
                default:
                    return "phone";
            }
        },

        // SIP Methods
        async initializeSIP() {
            if (!this.sipConfig) {
                log.error("SIP config not available");
                return;
            }

            // Eğer aynı config ile zaten bağlıysak, yeniden bağlanmaya gerek yok
            if (
                this._currentSipConfig &&
                this._sipConfigsAreEqual(this._currentSipConfig, this.sipConfig) &&
                this.simpleUser &&
                this.sipStatus.isRegistered
            ) {
                log("SIP already connected with same config, skipping reconnection");
                return;
            }

            // Mevcut bağlantıyı kapat
            if (this.simpleUser) {
                this.simpleUser.disconnect();
                this.simpleUser = null;
            }

            try {
                const delegate = {
                    onServerConnect: () => {
                        log("SIP server connected");
                        this.sipStatus.connectionStatus = "Sunucuya bağlandı";
                    },

                    onServerDisconnect: (error) => {
                        log("SIP server disconnected", error);
                        this.sipStatus.isRegistered = false;
                        this.sipStatus.connectionStatus = "Bağlantı kesildi";
                        this._currentSipConfig = null;
                        this.scheduleReconnect();
                    },

                    onRegistered: () => {
                        log("SIP registered successfully");
                        this.sipStatus.isRegistered = true;
                        this.sipStatus.connectionStatus = "Kayıt başarılı";
                        this.sipStatus.extensionNumber = this.sipConfig.username;
                        this.sipStatus.domain = this.sipConfig.domain;
                        // Başarılı bağlantı sonrası current config'i kaydet
                        this._currentSipConfig = { ...this.sipConfig };
                    },

                    onUnregistered: () => {
                        log("SIP unregistered");
                        this.sipStatus.isRegistered = false;
                        this.sipStatus.connectionStatus = "Kayıt silindi";
                        this._currentSipConfig = null;
                    },

                    onRegistrationFailed: (error) => {
                        log.error("SIP registration failed:", error);
                        this.sipStatus.isRegistered = false;
                        this.sipStatus.connectionStatus = "Kayıt başarısız: " + (error.message || error);
                        this._currentSipConfig = null;
                        this.scheduleReconnect();
                    },

                    onCallCreated: () => {
                        log("Call created");
                    },

                    onCallReceived: (e) => {
                        log("Incoming call received", e);
                        this.handleIncomingCall();
                    },

                    onCallAnswered: (e) => {
                        log("Call answered", e);
                        this.handleCallAnswered();
                    },

                    onCallHangup: (e) => {
                        log("Call hangup event", e);
                        this.handleCallHangup();
                    },

                    onCallHold: (e) => {
                        log("Call hold callback", e);
                        // isOnHold state handleHold metodunda yönetiliyor
                    },

                    onCallUnhold: (e) => {
                        log("Call unhold callback", e);
                        // isOnHold state handleHold metodunda yönetiliyor
                    },
                };

                const options = {
                    aor: this.sipConfig.aor,
                    delegate: delegate,
                    media: {
                        constraints: { audio: true, video: false },
                        remote: {
                            audio: this.$refs.remoteAudio,
                        },
                    },
                    userAgentOptions: {
                        authorizationUsername: this.sipConfig.username,
                        authorizationPassword: this.sipConfig.password,
                        displayName: this.sipConfig.displayName,
                        logLevel: "warn",
                        userAgentString: "Supsis Ai WebPhone - v0.21",
                    },
                };

                this.simpleUser = new SimpleUser(this.sipConfig.server, options);

                await this.simpleUser.connect();
                await this.simpleUser.register();
            } catch (error) {
                log.error("SIP initialization error:", error);
                this.sipStatus.connectionStatus = "Başlatma hatası: " + error.message;
                this.scheduleReconnect();
            }
        },

        getCurrentUserExtension() {
            try {
                const channel = this.netGsmChannel;
                const mapping = channel.payload?.mapping || {};
                const loggedUserId = this.user._id;
                const extension = Object.entries(mapping).find(([_, userId]) => userId === loggedUserId);
                const foundExtension = this.extensions.find((ext) => ext.extension === extension[0]);
                log("getCurrentUserExtension", {
                    channel,
                    mapping,
                    mappingEntries: Object.entries(mapping),
                    loggedUserId,
                    extension,
                    foundExtension,
                    extensions: this.extensions,
                });
                return foundExtension;
            } catch (error) {
                log.error("Failed to get extension:", error);
                return null;
            }
        },

        scheduleReconnect() {
            // Mola modundayken otomatik yeniden bağlanma
            if (this.isOnBreak) {
                return;
            }

            if (this.retryTimer) {
                clearTimeout(this.retryTimer);
            }

            this.retryTimer = setTimeout(() => {
                if (this.shouldShowWebPhone && !this.sipStatus.isRegistered && !this.isOnBreak) {
                    log("Attempting to reconnect...");
                    this.initializeSIP();
                }
            }, 5000);
        },

        // Call Methods
        handleDial(digit) {
            log("handleDial called", {
                digit: digit,
                dialNumber: this.dialNumber,
                simpleUser: this.simpleUser,
                sipStatus: this.sipStatus,
            });
            this.dialNumber += digit;
        },

        async handleCall(number = null) {
            log("handleCall called", {
                number: number,
                dialNumber: this.dialNumber,
                simpleUser: this.simpleUser,
                sipStatus: this.sipStatus,
                isOnBreak: this.isOnBreak,
            });

            if (this.isOnBreak) {
                this.$message.warning("Mola modundayken arama yapılamaz! Önce online olun.");
                return;
            }

            const targetNumber = number || this.dialNumber;

            if (!this.simpleUser || !this.sipStatus.isRegistered) {
                this.$message.error("SIP bağlantısı yok!");
                return;
            }

            if (!targetNumber.trim()) {
                this.$message.error("Lütfen bir numara girin!");
                return;
            }

            try {
                await this.simpleUser.call(`sip:${targetNumber}@${this.sipConfig.domain}`);

                this.callState = {
                    ...this.callState,
                    status: "calling",
                    currentSession: this.simpleUser.session,
                    callerNumber: targetNumber,
                    callStartTime: new Date(),
                };

                this.playRingbackTone();
                this.addToCallHistory(targetNumber, "outgoing");

                // Auto-open if minimized
                if (this.isMinimized) {
                    this.isMinimized = false;
                }
            } catch (error) {
                log.error("Call failed:", error);
                this.$message.error("Arama hatası: " + error.message);
            }
        },

        handleHangup() {
            log("hangup called manually", {
                simpleUser: this.simpleUser,
                callState: this.callState,
                currentSession: this.callState.currentSession,
            });
            if (this.simpleUser && this.callState.currentSession) {
                try {
                    log("hangup called", this.callState.currentSession);
                    this.simpleUser.hangup();
                } catch (error) {
                    log.error("hangup error:", error);
                }
            }

            // Force callState güncelleme (SIP event gelmezse diye)
            setTimeout(() => {
                if (this.callState.status !== "idle") {
                    this.handleCallHangup();
                }
            }, 1000);
        },

        handleAnswer() {
            log("answer called manually", {
                simpleUser: this.simpleUser,
                callState: this.callState,
                currentSession: this.callState.currentSession,
            });
            if (this.simpleUser && this.callState.status === "incoming") {
                try {
                    this.simpleUser.answer();
                } catch (error) {
                    log.error("Answer failed:", error);
                }
            }
        },

        handleReject() {
            log("reject called manually", {
                simpleUser: this.simpleUser,
                callState: this.callState,
                currentSession: this.callState.currentSession,
                keys: Object.keys(this.callState?.currentSession || {}),
            });
            if (this.simpleUser && this.callState.status === "incoming") {
                try {
                    log("reject called", this.callState.currentSession);
                    this.simpleUser.decline();
                } catch (error) {
                    log.error("Reject failed:", error);
                }
            }

            // Force callState güncelleme (SIP event gelmezse diye)
            setTimeout(() => {
                if (this.callState.status !== "idle") {
                    this.handleCallHangup();
                }
            }, 1000);
        },

        handleMute() {
            log("mute called manually", {
                simpleUser: this.simpleUser,
                callState: this.callState,
                currentSession: this.callState.currentSession,
            });
            if (!this.simpleUser || !this.callState.currentSession) return;

            try {
                if (this.callState.isMuted) {
                    this.simpleUser.unmute();
                    this.callState.isMuted = false;
                } else {
                    this.simpleUser.mute();
                    this.callState.isMuted = true;
                }
            } catch (error) {
                log.error("Mute toggle error:", error);
            }
        },

        handleHold() {
            log("hold called manually", {
                simpleUser: this.simpleUser,
                callState: this.callState,
                currentSession: this.callState.currentSession,
            });
            if (!this.simpleUser || !this.callState.currentSession) return;

            try {
                if (this.callState.isOnHold) {
                    this.simpleUser.unhold();
                    this.callState.isOnHold = false;
                } else {
                    this.simpleUser.hold();
                    this.callState.isOnHold = true;
                }
            } catch (error) {
                log.error("Hold toggle error:", error);
            }
        },

        async handleTransfer(targetExtension) {
            log("transfer called manually", {
                simpleUser: this.simpleUser,
                callState: this.callState,
                currentSession: this.callState.currentSession,
            });

            if (!this.callState.currentSession) {
                this.$message.error("Aktif çağrı bulunamadı!");
                return;
            }

            if (!this.callState.callerNumber) {
                this.$message.error("Arama numarası bulunamadı!");
                return;
            }

            try {
                // Kendi dahilimizin bilgisini al
                const currentExtension = this.getCurrentUserExtension();
                if (!currentExtension) {
                    this.$message.error("Dahili bilgisi bulunamadı!");
                    return;
                }

                log("Transferring call via NetGSM API", {
                    currentExtension: currentExtension.extension,
                    targetExtension,
                    callerNumber: this.callState.callerNumber,
                });

                const histories = this.netgsmHistory || [];
                // Raw payload formatında history'den ara: { channelId, data: { customer_num, unique_id, ... } }
                const latestHistory = histories.find((history) => {
                    return history.channelId === this.netGsmChannel._id && 
                           history.data?.customer_num === this.callState.callerNumber;
                });
                console.log("En güncel history:", latestHistory);
                const uniqueId = latestHistory?.data?.unique_id || null;


                // NetGSM API'sine transfer isteği gönder
                // Backend CDR'dan müşteri numarasına göre aktif çağrıyı bulup uniqueId'yi alacak
                await WEBPHONE_NETGSM_API.calls.redirect({
                    extension: currentExtension.extension,
                    redirectexten: targetExtension,
                    callednumber: this.callState.callerNumber, // Müşteri numarası - backend bununla CDR'da arayacak,
                    uniqueid: uniqueId,
                });

                this.$message.success(`Arama ${targetExtension} numaralı dahiliye yönlendirildi`);
                log("Call transferred successfully to:", targetExtension);
            } catch (error) {
                log.error("Transfer failed:", error);
                this.$message.error("Arama yönlendirilemedi: " + (error.message || error));
            }
        },

        handleDTMF(digit) {
            log("dtmf called manually", {
                simpleUser: this.simpleUser,
                callState: this.callState,
                currentSession: this.callState.currentSession,
                digit: digit,
            });
            // DTMF gönder (ses zaten handleKeyPress'te çalıyor)
            if (this.callState.status === "connected" && this.simpleUser) {
                try {
                    this.simpleUser.sendDTMF(digit);
                } catch (error) {
                    log.error("DTMF send error:", error);
                }
            }
        },

        // Call Event Handlers
        handleIncomingCall() {
            log("handleIncomingCall started", {
                session: this.simpleUser.session,
                isMinimized: this.isMinimized,
                simpleUser: this.simpleUser,
                callState: this.callState,
            });

            if (this.simpleUser && this.simpleUser.session) {
                // Auto-open if minimized
                if (this.isMinimized) {
                    this.isMinimized = false;
                    this.focusDialerInput();
                }

                const remoteIdentity = this.simpleUser.session.remoteIdentity;
                const callerNumber = remoteIdentity ? remoteIdentity.uri.user : "Bilinmeyen";

                log("Incoming call from:", callerNumber);

                this.callState = {
                    ...this.callState,
                    status: "incoming",
                    currentSession: this.simpleUser.session,
                    callerNumber: callerNumber,
                    callerName: this.getContactName(callerNumber),
                };

                log("Playing ringtone...");
                this.playRingtone();
                this.addToCallHistory(callerNumber, "incoming");
            } else {
                log.error("handleIncomingCall: No simpleUser or session");
            }
        },

        handleCallAnswered() {
            this.stopAllTones();
            this.callState = {
                ...this.callState,
                status: "connected",
                callStartTime: new Date(),
            };
            this.startCallDurationTimer();
        },

        handleCallHangup() {
            log("handleCallHangup called - cleaning up call state");

            this.stopAllTones();
            this.stopCallDurationTimer();

            this.callState = {
                status: "idle",
                currentSession: null,
                isOnHold: false,
                isMuted: false,
                callerNumber: null,
                callerName: null,
                callStartTime: null,
                callDuration: 0,
            };

            // Clear dial number after call
            this.dialNumber = "";

            log("Call state reset to idle");
        },

        // Audio Methods
        playRingtone() {
            log("playRingtone called");

            try {
                // Önce Howl objesini dene
                if (this.sounds.incomingCall) {
                    log("Playing Howl incoming call sound");
                    this.sounds.incomingCall.play();
                } else {
                    log.error("Howl incomingCall object not available");
                }
            } catch (error) {
                log.error("Ringtone play error:", error);
            }
        },

        playRingbackTone() {
            if (this.$refs.ringbacktone) {
                this.$refs.ringbacktone.play().catch((e) => log("Ringback play error:", e));
            }
        },

        stopAllTones() {
            try {
                // Howl objelerini durdur
                if (this.sounds.incomingCall) {
                    this.sounds.incomingCall.stop();
                }
                if (this.sounds.ringback) {
                    this.sounds.ringback.stop();
                }

                // HTML5 audio elementlerini durdur
                if (this.$refs.ringtone) {
                    this.$refs.ringtone.pause();
                    this.$refs.ringtone.currentTime = 0;
                }
                if (this.$refs.ringbacktone) {
                    this.$refs.ringbacktone.pause();
                    this.$refs.ringbacktone.currentTime = 0;
                }

                // DTMF generator'ı durdur
                dtmfGenerator.stop();
            } catch (error) {
                log.error("Stop tones error:", error);
            }
        },

        playDTMFTone(digit = "1") {
            try {
                dtmfGenerator.playTone(digit, 150);
            } catch (error) {
                log.error("DTMF tone play error:", error);
            }
        },

        // Call Duration Timer
        startCallDurationTimer() {
            this.callDurationTimer = setInterval(() => {
                if (this.callState.callStartTime) {
                    this.callState.callDuration = Math.floor((new Date() - this.callState.callStartTime) / 1000);
                }
            }, 1000);
        },

        stopCallDurationTimer() {
            if (this.callDurationTimer) {
                clearInterval(this.callDurationTimer);
                this.callDurationTimer = null;
            }
        },

        // Call History Methods
        addToCallHistory(number, type) {
            const historyItem = {
                number: number,
                name: this.getContactName(number),
                type: type, // incoming, outgoing, missed
                date: new Date(),
                duration: this.callState.callDuration,
            };

            this.callHistory.unshift(historyItem);

            // Keep only last 50 calls
            if (this.callHistory.length > 50) {
                this.callHistory = this.callHistory.slice(0, 50);
            }

            this.saveCallHistory();
        },

        loadCallHistory() {
            try {
                const saved = localStorage.getItem(`webphone_netgsm_call_history_${this.user._id}`);
                if (saved) {
                    this.callHistory = JSON.parse(saved);
                }
            } catch (error) {
                log.error("Call history load error:", error);
            }
        },

        saveCallHistory() {
            try {
                localStorage.setItem(`webphone_netgsm_call_history_${this.user._id}`, JSON.stringify(this.callHistory));
            } catch (error) {
                log.error("Call history save error:", error);
            }
        },

        clearCallHistory() {
            this.callHistory = [];
            this.saveCallHistory();
        },

        handleCallFromHistory(call) {
            this.dialNumber = call.number;
            this.activeTab = "dialer";
            this.handleCall(call.number);
        },

        handleCallFromContacts(contact) {
            this.dialNumber = contact.number;
            this.activeTab = "dialer";
            this.handleCall(contact.number);
        },

        // Contacts Methods
        getContactName(number) {
            // Dahililerden isim almaya çalış
            const extension = this.extensions.find((ext) => ext.extension === number);
            if (extension) return extension.displayname;

            // Mapping'den kullanıcı ismini al
            const mapping = this.netGsmChannel?.payload?.mapping || {};
            const userId = mapping[number];
            if (userId) {
                const user = this.users?.find((u) => u._id === userId);
                if (user) return user.name;
            }

            return null;
        },

        // External Call Request Handler
        handleExternalCallRequest(payload) {
            log("External call request received:", payload);

            // Eğer payload'da channelId varsa, sadece o kanal işlem yapsın
            if (payload.channelId) {
                if (this.netGsmChannel && this.netGsmChannel._id === payload.channelId) {
                    this.initiateCall(payload.phoneNumber);
                }
                // channelId belirtilmişse ve bu kanal değilse ignore et
                return;
            }

            if (!this.shouldShowWebPhone) {
                log.error("Webphone not available for external call");
                return;
            }

            if (this.callState.status !== "idle") {
                log.error("Cannot start external call - webphone is busy");
                return;
            }

            this.initiateCall(payload.phoneNumber);
        },

        initiateCall(phoneNumber) {
            // Webphone'u açık moda geçir
            if (this.isMinimized) {
                this.isMinimized = false;
            }

            // Dialer tab'ına geç
            this.activeTab = "dialer";

            // Numarayı set et
            this.dialNumber = phoneNumber;

            // Input'a focus ver
            this.focusDialerInput();

            // Otomatik aramayı başlat
            setTimeout(() => {
                this.handleCall(phoneNumber);
            }, 500);

            log("External call initiated for:", phoneNumber);
        },

        getActivePhoneNumber() {
            // Aktif çağrıdaki telefon numarasını döndür
            if (this.callState.callerNumber) {
                const phoneNumber = this.callState.callerNumber.replace("@c.us", "").replace("sip:", "").split("@")[0];
                return phoneNumber;
            }

            return null;
        },

        // Cleanup
        disconnect() {
            if (this.simpleUser) {
                this.simpleUser.disconnect();
                this.simpleUser = null;
            }
            this.stopCallDurationTimer();
            this.stopAllTones();
            if (this.retryTimer) {
                clearTimeout(this.retryTimer);
            }
            // SIP config cache'ini temizle
            this._cachedSipConfig = null;
            this._currentSipConfig = null;
        },
    },
    async mounted() {
        // System PubSub listener for incoming call requests
        this.systemPubSubSubscription = SYSTEM_PUBSUB.stream.subscribe((message) => {
            if (message.type === SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START) {
                log("handleExternalCallRequest", message.payload);
                this.handleExternalCallRequest(message.payload);
            }
        });

        this.loadCallHistory();

        // Window resize handler for drag boundaries
        window.addEventListener("resize", () => {
            // Update minimized position boundaries
            this.minimizedPosition = {
                bottom: Math.max(10, Math.min(window.innerHeight - 70, this.minimizedPosition.bottom)),
                right: Math.max(10, Math.min(window.innerWidth - 70, this.minimizedPosition.right)),
            };

            // Update maximized position boundaries
            const webphoneWidth = 320;
            const webphoneHeight = 650;
            this.maximizedPosition = {
                bottom: Math.max(10, Math.min(window.innerHeight - webphoneHeight - 10, this.maximizedPosition.bottom)),
                right: Math.max(10, Math.min(window.innerWidth - webphoneWidth - 10, this.maximizedPosition.right)),
            };
        });
        // Dahili listesini mount'ta bir kere al
        await this.fetchExtensions();

        if (this.shouldShowWebPhone) {
            await this.initializeSIP();
        }

        // Click outside handler for dropdown
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        this.disconnect();
        // System PubSub subscription'ını temizle
        if (this.systemPubSubSubscription) {
            this.systemPubSubSubscription.unsubscribe();
            this.systemPubSubSubscription = null;
        }
        // Click outside handler'ını temizle
        document.removeEventListener('click', this.handleClickOutside);
    },
    watch: {
        shouldShowWebPhone(newVal, oldVal) {
            if (newVal && !oldVal) {
                this.initializeSIP();
            } else if (!newVal && oldVal) {
                this.disconnect();
            }
        },

        netGsmChannel: {
            handler(newVal) {
                if (newVal && this.userExtensionMapping) {
                    this.disconnect();
                    this.$nextTick(() => {
                        this.initializeSIP();
                    });
                }
            },
            deep: true,
        },

        activeTab(newVal) {
            if (newVal === "dialer" && !this.isMinimized) {
                this.focusDialerInput();
            }
        },

        foundContacts(newVal) {
            if (newVal.length > 0) {
                this.$nextTick(() => {
                    this.contactToggleElement = this.$refs.contactToggleButton;
                });
            } else {
                this.contactToggleElement = null;
            }
        },
    },
});
</script>

<style scoped lang="scss">
.webphone-container {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
    user-select: none;
    z-index: 9999;
    &.shouldShowContactOverlay {
        .contact-overlay-toggle-container {
            left: 75%;
        }
    }
}

// Minimized Mode
.webphone-minimized {
    position: fixed;
    z-index: 9999;
}

.webphone-minimized-button {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, #007c89, #005a66); // NetGSM yeşil-mavi teması
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    box-shadow: 0 4px 20px rgba(0, 124, 137, 0.3);
    transition: all 0.3s ease;
    position: relative;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 25px rgba(0, 124, 137, 0.4);
    }

    &:active {
        cursor: grabbing;
    }

    .material-icons {
        color: white;
        font-size: 28px;
    }

    .status-indicator {
        position: absolute;
        top: 5px;
        right: 5px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #ff3b30;
        border: 2px solid white;
    }

    &.status-connected .status-indicator {
        background: #34c759;
    }

    &.status-on-break .status-indicator {
        background: #ff9500; // Orange for break mode
    }

    &.status-calling,
    &.status-ringing {
        animation: pulse 1.5s infinite;
    }

    &.incoming-call {
        animation: shake 0.5s infinite alternate;
        background: linear-gradient(135deg, #ff3b30, #d70015);
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
    }
}

@keyframes shake {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(3px);
    }
}

// Maximized Mode
.webphone-maximized-wrapper {
    position: fixed;
    max-width: 620px; // Default width, will be overridden by computed style
    height: 650px;
    border-radius: 20px;
    overflow: visible;
    z-index: 9999;
    display: flex;
    transition: width 0.3s ease;
    background: transparent; // Wrapper background transparan
}

.contact-overlay-left {
    width: 300px; // 300px genişlik
    background: none !important; // Tamamen şeffaf - force override
    transition: width 0.3s ease;

    &.collapsed {
        width: 0;
        overflow: hidden;
    }
}

.webphone-maximized-content {
    flex: 1;
    width: 320px; // Sabit 320px genişlik
    min-width: 320px; // Sabit 320px genişlik
    background: white; // Background burada
    border-radius: 20px; // Border radius burada
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.webphone-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
    background: linear-gradient(135deg, #e8f4f5, #d1e7e9); // NetGSM teması
    border-bottom: 1px solid #b8d4d7;
    cursor: grab;
    overflow: visible;
    position: relative;

    &:active {
        cursor: grabbing;
    }

    .header-center h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #1c1c1e;
    }

    .status-dropdown {
        position: relative;

        .status-display {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: #8e8e93;
            cursor: pointer;
            padding: 4px 8px;
            border-radius: 6px;
            transition: background-color 0.2s ease;

            &:hover {
                background: rgba(0, 0, 0, 0.05);
            }

            .status-dot {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: #ff3b30;
            }

            &.connected .status-dot {
                background: #34c759;
            }

            &.on-break .status-dot {
                background: #ff9500;
            }

            .dropdown-arrow {
                font-size: 16px;
                transition: transform 0.2s ease;
            }
        }

        &.open .status-display .dropdown-arrow {
            transform: rotate(180deg);
        }

        .dropdown-menu {
            display: flex;
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, #e8f4f5, #d1e7e9); // NetGSM teması
            border: 1px solid #b8d4d7;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 124, 137, 0.3);
            z-index: 10001;
            margin-top: 4px;
            overflow: visible;
            min-width: 140px;

            .dropdown-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 12px 16px;
                font-size: 13px;
                cursor: pointer;
                transition: background-color 0.2s ease;

                &:hover {
                    background: rgba(0, 124, 137, 0.1);
                }

                .status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;

                    &.connected {
                        background: #34c759;
                    }

                    &.break {
                        background: #ff9500;
                    }
                }

                &.break-option {
                    color: #ff9500;
                }

                &.online-option {
                    color: #34c759;
                }
            }
        }
    }

    .minimize-btn {
        background: none;
        border: none;
        padding: 5px;
        border-radius: 8px;
        cursor: pointer;

        &:hover {
            background: rgba(0, 0, 0, 0.05);
        }

        .material-icons {
            font-size: 20px;
            color: #8e8e93;
        }
    }
}

.webphone-tabs {
    display: flex;
    background: #e8f4f5; // NetGSM teması
    border-bottom: 1px solid #b8d4d7;

    .tab-btn {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding: 12px 8px;
        border: none;
        background: none;
        cursor: pointer;
        transition: all 0.2s ease;

        .material-icons {
            font-size: 20px;
            color: #8e8e93;
        }

        span {
            font-size: 11px;
            color: #8e8e93;
            font-weight: 500;
        }

        &.active {
            .material-icons,
            span {
                color: #007c89; // NetGSM ana rengi
            }
        }

        &:hover:not(.disabled) {
            background: rgba(0, 0, 0, 0.05);
        }

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;

            .material-icons,
            span {
                color: #c7c7cc !important;
            }
        }
    }

    &.disabled {
        opacity: 0.6;
        pointer-events: none;
    }
}

.webphone-content {
    flex: 1;
    overflow: hidden;
    position: relative;
    border-radius: 0 0 20px 20px; // Alt köşeleri yuvarlak yap

    &.disabled {
        opacity: 0.3;
    }
}

// Break Mode Overlay
.break-mode-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    z-index: 10;
    padding: 40px 20px;
    text-align: center;

    .break-icon {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: linear-gradient(135deg, #ff9500, #ff7b00);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 20px;
        box-shadow: 0 8px 25px rgba(255, 149, 0, 0.3);

        .material-icons {
            font-size: 36px;
            color: white;
        }
    }

    h3 {
        margin: 0 0 12px 0;
        font-size: 18px;
        font-weight: 600;
        color: #1c1c1e;
    }

    p {
        margin: 0 0 24px 0;
        font-size: 14px;
        color: #8e8e93;
        line-height: 1.4;
    }

    .online-button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        background: linear-gradient(135deg, #34c759, #2fb653);
        color: white;
        border: none;
        border-radius: 12px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(52, 199, 89, 0.3);

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(52, 199, 89, 0.4);
        }

        .material-icons {
            font-size: 18px;
        }
    }
}

// Incoming Call Notification
.incoming-call-notification {
    position: fixed;
    top: 20px;
    right: 20px;
    width: 300px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    padding: 20px;
    z-index: 10000;
    animation: slideIn 0.3s ease;

    .caller-info {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 15px;

        .caller-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #e5e5ea;
            display: flex;
            align-items: center;
            justify-content: center;

            .material-icons {
                color: #8e8e93;
                font-size: 20px;
            }
        }

        .caller-details {
            .caller-name {
                font-weight: 600;
                color: #1c1c1e;
                margin-bottom: 2px;
            }

            .caller-number {
                font-size: 14px;
                color: #8e8e93;
            }
        }
    }

    .call-actions {
        display: flex;
        gap: 12px;

        button {
            flex: 1;
            padding: 12px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;

            .material-icons {
                color: white;
                font-size: 20px;
            }
        }

        .answer-btn {
            background: #34c759;

            &:hover {
                background: #2fb653;
            }
        }

        .reject-btn {
            background: #ff3b30;

            &:hover {
                background: #e8322a;
            }
        }
    }
}

@keyframes slideIn {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

.webphone-dragging {
    transition: none !important;
    cursor: grabbing !important;
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .webphone-maximized-content {
        background: #1c1c1e;
        color: white;

        .toggle-button {
            background: linear-gradient(135deg, #3a3a3c, #1c1c1e);
            border-color: rgba(255, 255, 255, 0.1);
        }
    }

    .webphone-header {
        background: linear-gradient(135deg, #2c2c2e, #1c1c1e);
        border-bottom-color: #3a3a3c;

        .header-center h3 {
            color: white;
        }
    }

    .webphone-tabs {
        background: #2c2c2e;
        border-bottom-color: #3a3a3c;
    }

    .status-dropdown .dropdown-menu {
        background: linear-gradient(135deg, #2c2c2e, #1c1c1e);
        border-color: #3a3a3c;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

        .dropdown-item {
            color: white;

            &:hover {
                background: rgba(0, 124, 137, 0.2);
            }
        }
    }

    .break-mode-overlay {
        background: rgba(28, 28, 30, 0.95);
        color: white;

        h3 {
            color: white;
        }

        p {
            color: #8e8e93;
        }
    }

    .incoming-call-notification {
        background: #1c1c1e;
        color: white;

        .caller-avatar {
            background: #3a3a3c;
        }
    }
}

.contact-overlay-toggle-container {
    position: absolute;
    bottom: 90px;
    left: 50%;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    z-index: 10;
    pointer-events: all;
    width: 200px;

    .toggle-button {
        display: flex;
        align-items: center;
        gap: 8px;
        background: linear-gradient(135deg, #007c89 0%, #005a66 100%); // NetGSM teması
        color: white;
        padding: 10px 16px;
        border-radius: 20px;
        cursor: pointer;
        box-shadow: 0 4px 20px rgba(0, 124, 137, 0.3);
        transition: all 0.3s ease;
        font-size: 13px;
        font-weight: 600;
        border: 2px solid rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);

        &:hover {
            transform: translateY(-2px) scale(1.02);
            box-shadow: 0 6px 25px rgba(0, 124, 137, 0.4);
            border-color: rgba(255, 255, 255, 0.3);
        }

        .material-icons {
            font-size: 18px;
        }

        .contact-count {
            background: rgba(255, 255, 255, 0.25);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 50%;
            min-width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: 700;
            margin-left: 4px;
        }
    }
}
</style>
